import styled, { keyframes } from 'styled-components';

const overlayFadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

export const DropArea = styled.div<{ $isDragging: boolean }>`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  text-align: center;
  padding: 32px 24px;
  border-radius: 14px;
  cursor: pointer;
  border: none;
  background: ${({ theme, $isDragging }) => {
    if ($isDragging) return theme.mode === 'dark' ? 'rgba(10,116,101,0.10)' : 'rgba(10,116,101,0.05)';
    return theme.mode === 'dark' ? '#18181b' : '#ffffff';
  }};
  transition: background 0.18s ease;
  color: ${({ theme }) => (theme.mode === 'dark' ? '#6b7280' : '#9ca3af')};
`;

export const DropTitle = styled.p`
  margin: 4px 0 0;
  font-size: 15px;
  font-weight: 300;
  line-height: 1.3;
  color: ${({ theme }) => (theme.mode === 'dark' ? '#f4f4f5' : '#111827')};
`;

/* ---- Układ dwóch boksów: [upload] | [biblioteka] ---- */
export const ChooseRow = styled.div`
  display: flex;
  align-items: stretch;
  width: 100%;
  @media (max-width: 560px) { flex-direction: column; }
`;

export const ChooseCol = styled.div`
  flex: 1 1 0;
  min-width: 0;
  display: flex;
  & > * { flex: 1; }
`;

export const ChooseDivider = styled.div`
  width: 1px;
  align-self: stretch;
  margin: 10px 6px;
  background: ${({ theme }) => (theme.mode === 'dark' ? '#3f3f46' : '#e5e7eb')};
  @media (max-width: 560px) { width: auto; height: 1px; margin: 6px 10px; }
`;

export const LibraryArt = styled.div`
  height: 104px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.colors?.primary?.[500] || '#0a7465'};
`;

/* ---- Wariant KOMPAKTOWY (inline, niski) — dwa krótkie boksy w rzędzie ---- */
export const CompactRow = styled.div`
  display: flex;
  width: 100%;
  position: relative;
  @media (max-width: 480px) { flex-direction: column; }
`;

export const CompactBox = styled.button<{ $isDragging?: boolean }>`
  flex: 1 1 0;
  min-width: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 12px 14px;
  min-height: 52px;
  border-radius: 10px;
  cursor: pointer;
  font-size: 13px;
  font-weight: 300;
  color: ${({ theme }) => (theme.mode === 'dark' ? '#e4e4e7' : '#374151')};
  border: 1px dashed ${({ theme, $isDragging }) => {
    if ($isDragging) return theme.colors?.primary?.[500] || '#0a7465';
    return theme.mode === 'dark' ? '#3f3f46' : '#d1d5db';
  }};
  background: ${({ theme, $isDragging }) => {
    if ($isDragging) return theme.mode === 'dark' ? 'rgba(10,116,101,0.10)' : 'rgba(10,116,101,0.05)';
    return theme.mode === 'dark' ? '#18181b' : '#ffffff';
  }};
  transition: border-color 0.18s ease, background 0.18s ease, color 0.18s ease;

  & svg { color: ${({ theme }) => theme.colors?.primary?.[500] || '#0a7465'}; flex-shrink: 0; }
  &:hover { border-color: ${({ theme }) => theme.colors?.primary?.[500] || '#0a7465'}; }
`;

export const CompactDivider = styled.div`
  width: 1px;
  align-self: stretch;
  margin: 8px 6px;
  background: ${({ theme }) => (theme.mode === 'dark' ? '#3f3f46' : '#e5e7eb')};
  @media (max-width: 480px) { width: auto; height: 1px; margin: 4px 8px; }
`;

export const DropDescription = styled.p`
  margin: 0;
  font-size: 13px;
  line-height: 1.5;
  color: ${({ theme }) => (theme.mode === 'dark' ? '#a1a1aa' : '#6b7280')};
`;

export const DropFormats = styled.p`
  margin: 2px 0 0;
  font-size: 11px;
  font-weight: 500;
  letter-spacing: 0.06em;
  color: ${({ theme }) => (theme.mode === 'dark' ? '#71717a' : '#9ca3af')};
`;

export const BrowseButton = styled.button`
  margin-top: 12px;
  padding: 8px 18px;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 300;
  /* Klasyczny czarny przycisk — spójny z komponentem AddButton. */
  color: #ffffff;
  background: #111827;
  border: 1px solid transparent;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.18);
  transition: background 0.2s ease, box-shadow 0.2s ease, transform 0.1s ease;

  &:hover { background: #000000; box-shadow: 0 2px 6px rgba(0, 0, 0, 0.28); }
  &:active { transform: scale(0.97); }

  ${({ theme }) => (theme.mode === 'dark'
    ? `
    background: #1f2937;
    border-color: #374151;

    &:hover { background: #111827; }
  `
    : '')}
`;

export const HiddenInput = styled.input`
  display: none;
`;

export const UploadingOverlay = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
  border-radius: 14px;
  font-size: 13px;
  font-weight: 500;
  color: ${({ theme }) => (theme.mode === 'dark' ? '#e4e4e7' : '#374151')};
  background: ${({ theme }) => (theme.mode === 'dark' ? 'rgba(24,24,27,0.86)' : 'rgba(255,255,255,0.86)')};
  backdrop-filter: blur(2px);
  animation: ${overlayFadeIn} 0.15s ease-out;
`;

/* ---- Krok kadrowania (react-easy-crop) ---- */
export const CropStage = styled.div`
  position: relative;
  width: 100%;
  height: 340px;
  background: #111827;
  border-radius: 14px;
  overflow: hidden;
`;

export const CropBar = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  margin-top: 16px;
`;

export const CropCounter = styled.span`
  flex-shrink: 0;
  font-size: 12px;
  font-weight: 300;
  font-variant-numeric: tabular-nums;
  color: ${({ theme }) => (theme.mode === 'dark' ? '#a1a1aa' : '#6b7280')};
`;

export const ZoomRange = styled.input`
  flex: 1;
  min-width: 0;
  accent-color: ${({ theme }) => theme.colors?.primary?.[500] || '#0a7465'};
  cursor: pointer;
`;

export const CropActions = styled.div`
  display: flex;
  gap: 8px;
  flex-shrink: 0;
`;

/* „Użyj oryginału" / „Anuluj" — ciemny (czarny) przycisk, spójny z AddButton. */
export const GhostButton = styled.button`
  padding: 8px 16px;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 300;
  color: #ffffff;
  background: #111827;
  border: 1px solid transparent;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.18);
  transition: background 0.2s ease, box-shadow 0.2s ease, transform 0.1s ease;

  &:hover:not(:disabled) { background: #000000; box-shadow: 0 2px 6px rgba(0, 0, 0, 0.28); }
  &:active:not(:disabled) { transform: scale(0.97); }
  &:disabled { opacity: 0.6; cursor: not-allowed; }

  ${({ theme }) => (theme.mode === 'dark'
    ? `
    background: #1f2937;
    border-color: #374151;

    &:hover:not(:disabled) { background: #111827; }
  `
    : '')}
`;

/* „Przytnij" — jasny przycisk z ramką (light), obok ciemnego „Użyj oryginału". */
export const PrimaryButton = styled.button`
  padding: 8px 18px;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 300;
  color: ${({ theme }) => (theme.mode === 'dark' ? '#d4d4d8' : '#374151')};
  background: transparent;
  border: 1px solid ${({ theme }) => (theme.mode === 'dark' ? '#3f3f46' : '#d1d5db')};
  transition: background 0.15s ease, border-color 0.15s ease;

  &:hover:not(:disabled) {
    background: ${({ theme }) => (theme.mode === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)')};
    border-color: ${({ theme }) => (theme.mode === 'dark' ? '#52525b' : '#9ca3af')};
  }
  &:disabled { opacity: 0.6; cursor: not-allowed; }
`;
